/*
 * Crew Ready Apparel: delivery date line under Add To Cart (Evan 2026-09-28, fix 5).
 * Shows "At your door about Oct 13 to 16" + "Free mockup by Tue, Sep 29. Approve it that day to get this date."
 * Deploy as assets/crew-ship-date.js. Markup: snippets/crew-ship-date.liquid. Rule: rule.md. Tests: node test.js
 *
 * Everything is counted in shop time (America/New_York), whatever the visitor's clock says.
 * The rule, in order:
 *   1. Mockup   = the next business day after the day you order (ET).
 *   2. You OK   = the same day you get the mockup.
 *   3. Ready    = 7 to 10 business days after you OK it (the store's own words). It ships that day.
 *   4. Door     = ready + ground transit in business days, picked from the visitor's time zone:
 *                 Eastern 3, Central 4, Mountain 5, Pacific 5, unknown 5.
 *                 Alaska, Hawaii and the territories get "Ready to ship about ..." instead (no ground estimate).
 * Business days skip Saturdays, Sundays and the holidays below. One holiday list serves the shop and the carrier.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else if (root && !root.CrewShipDate) {
    root.CrewShipDate = api;
    api.boot();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var CONFIG = {
    shopTimeZone: 'America/New_York',
    // Orders at or after this hour (ET, 24h clock) on a business day count as the next day's orders.
    // 24 = no cutoff: any time on Monday gets a Tuesday mockup (the store promises "within one business day",
    // and order #1004, placed 11:14 pm ET, got its mockup the next afternoon). Set 14 for a 2 pm cutoff.
    cutoffHour: 24,
    mockupDays: 1,      // business days from the order day to the mockup email
    approveDays: 0,     // business days from the mockup to the customer's OK (0 = same day)
    readyMin: 7,        // business days from the OK to ready (and shipped)
    readyMax: 10,
    // Ground transit from Naples FL / Charlotte NC, in business days after the ship day.
    transitDays: { east: 3, central: 4, mountain: 5, pacific: 5, unknown: 5 },
    // Shop + carrier closed. YYYY-MM-DD, observed dates. Years listed here are used exactly as written
    // (add a shop closure by adding its date). Any year NOT listed falls back to the same rules, generated.
    holidays: [
      '2026-01-01', // New Year's Day
      '2026-05-25', // Memorial Day
      '2026-07-03', // Independence Day (July 4 is a Saturday)
      '2026-09-07', // Labor Day
      '2026-11-26', // Thanksgiving
      '2026-11-27', // Day after Thanksgiving
      '2026-12-24', // Christmas Eve
      '2026-12-25', // Christmas Day
      '2026-12-31', // New Year's Eve
      '2027-01-01', // New Year's Day
      '2027-05-31', // Memorial Day
      '2027-07-05', // Independence Day (July 4 is a Sunday)
      '2027-09-06', // Labor Day
      '2027-11-25', // Thanksgiving
      '2027-11-26', // Day after Thanksgiving
      '2027-12-24', // Christmas Eve (also Christmas observed; Dec 25 is a Saturday)
      '2027-12-31'  // New Year's Eve (also New Year's Day 2028 observed; Jan 1 is a Saturday)
    ],
    text: {
      door: 'At your door about {range}',
      shipOnly: 'Ready to ship about {range}',
      sub: 'Free mockup by {mockup}. Approve it that day to get this date.'
    }
  };

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MS_DAY = 86400000;

  // ---- day numbers (days since 1970-01-01, no time zone involved) ----
  function dayNum(y, m, d) { return Math.floor(Date.UTC(y, m - 1, d) / MS_DAY); }
  function fromDayNum(n) {
    var dt = new Date(n * MS_DAY);
    return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate(), wd: dt.getUTCDay() };
  }
  function weekday(n) { return ((n % 7) + 7 + 4) % 7; } // 1970-01-01 was a Thursday; 0 = Sunday
  function iso(n) {
    var p = fromDayNum(n);
    return p.y + '-' + (p.m < 10 ? '0' : '') + p.m + '-' + (p.d < 10 ? '0' : '') + p.d;
  }
  function parseIso(s) { var a = s.split('-'); return dayNum(+a[0], +a[1], +a[2]); }

  // ---- shop clock ----
  // Wall-clock date and hour in the shop's zone. Intl first; a fixed US Eastern rule if Intl can't do zones.
  function shopNow(date, tz, noIntl) {
    if (!noIntl) {
      try {
        var f = new Intl.DateTimeFormat('en-US', {
          timeZone: tz || CONFIG.shopTimeZone, year: 'numeric', month: 'numeric', day: 'numeric',
          hour: 'numeric', minute: 'numeric', hour12: false
        });
        var o = {};
        f.formatToParts(date).forEach(function (p) { o[p.type] = p.value; });
        var h = +o.hour; if (h === 24) h = 0; // some engines print midnight as 24
        if (o.year && o.month && o.day && !isNaN(h)) return { day: dayNum(+o.year, +o.month, +o.day), hour: h, minute: +o.minute };
      } catch (e) { /* fall through */ }
    }
    return easternFallback(date);
  }
  // US Eastern: UTC-4 from the 2nd Sunday of March 2:00 local (07:00 UTC) to the 1st Sunday of November 2:00 local (06:00 UTC).
  function easternFallback(date) {
    var t = date.getTime();
    var y = new Date(t).getUTCFullYear();
    var mar1 = dayNum(y, 3, 1), nov1 = dayNum(y, 11, 1);
    var dstStartDay = mar1 + ((7 - weekday(mar1)) % 7) + 7;
    var dstEndDay = nov1 + ((7 - weekday(nov1)) % 7);
    var start = dstStartDay * MS_DAY + 7 * 3600000, end = dstEndDay * MS_DAY + 6 * 3600000;
    var local = new Date(t + ((t >= start && t < end) ? -4 : -5) * 3600000);
    return {
      day: dayNum(local.getUTCFullYear(), local.getUTCMonth() + 1, local.getUTCDate()),
      hour: local.getUTCHours(), minute: local.getUTCMinutes()
    };
  }

  // ---- holidays ----
  function nthWeekday(y, m, wd, nth) { // nth 1..5, or -1 for the last one
    if (nth > 0) { var first = dayNum(y, m, 1); return first + ((wd - weekday(first) + 7) % 7) + (nth - 1) * 7; }
    var last = dayNum(y, m + 1, 1) - 1; return last - ((weekday(last) - wd + 7) % 7);
  }
  function observed(n) { var w = weekday(n); return w === 6 ? n - 1 : (w === 0 ? n + 1 : n); }
  // The same rules as the list in CONFIG, for any year.
  function rulesHolidays(y) {
    var out = [observed(dayNum(y, 1, 1)), nthWeekday(y, 5, 1, -1), observed(dayNum(y, 7, 4)), nthWeekday(y, 9, 1, 1)];
    var tg = nthWeekday(y, 11, 4, 4); out.push(tg, tg + 1);
    var eve = dayNum(y, 12, 24), xmas = dayNum(y, 12, 25), nye = dayNum(y, 12, 31);
    if (weekday(eve) > 0 && weekday(eve) < 6) out.push(eve);
    out.push(observed(xmas));
    if (weekday(nye) > 0 && weekday(nye) < 6) out.push(nye);
    return out;
  }
  function holidaySet(list, fromYear, toYear) {
    var set = {}, listed = {};
    (list || []).forEach(function (s) { set[parseIso(s)] = true; listed[+s.slice(0, 4)] = true; });
    for (var y = fromYear - 1; y <= toYear + 1; y++) {
      if (listed[y]) continue;
      rulesHolidays(y).forEach(function (n) {
        var ny = fromDayNum(n).y; // New Year's observed can land on Dec 31 of the year before
        if (!listed[ny]) set[n] = true;
      });
    }
    return set;
  }

  // ---- business days ----
  function isBusinessDay(n, hol) { var w = weekday(n); return w !== 0 && w !== 6 && !hol[n]; }
  function addBusinessDays(n, k, hol) { // k business days strictly after day n
    var guard = 0;
    while (k > 0 && guard++ < 400) { n++; if (isBusinessDay(n, hol)) k--; }
    return n;
  }

  // ---- visitor region (only picks the ground transit days) ----
  var CENTRAL_IN = { 'America/Indiana/Knox': 1, 'America/Indiana/Tell_City': 1, 'America/Knox_IN': 1, 'US/Indiana-Starke': 1 };
  function zoneRegion(tz) {
    if (!tz || typeof tz !== 'string') return 'unknown';
    if (CENTRAL_IN[tz]) return 'central';
    if (/^(America\/(New_York|Detroit|Fort_Wayne|Louisville|Indianapolis|Kentucky\/|Indiana\/)|US\/(Eastern|Michigan|East-Indiana)|EST5EDT$|EST$)/.test(tz)) return 'east';
    if (/^(America\/(Chicago|Menominee|North_Dakota\/)|US\/Central|CST6CDT$)/.test(tz)) return 'central';
    if (/^(America\/(Denver|Boise|Phoenix|Shiprock)|US\/(Mountain|Arizona)|MST7MDT$|MST$|Navajo$)/.test(tz)) return 'mountain';
    if (/^(America\/Los_Angeles|US\/Pacific|PST8PDT$)/.test(tz)) return 'pacific';
    if (/^(America\/(Anchorage|Juneau|Sitka|Yakutat|Nome|Metlakatla|Adak|Atka|Puerto_Rico|St_Thomas|Virgin)|Pacific\/(Honolulu|Johnston|Guam|Saipan|Pago_Pago|Midway)|US\/(Alaska|Aleutian|Hawaii|Samoa)|HST$)/.test(tz)) return 'far';
    return 'unknown';
  }
  function visitorZone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { return ''; }
  }

  // ---- the rule ----
  // opts: { zone, readyMin, readyMax, cutoffHour, noIntl }
  function compute(date, opts) {
    opts = opts || {};
    var c = CONFIG;
    var now = shopNow(date, c.shopTimeZone, opts.noIntl);
    var hol = holidaySet(c.holidays, fromDayNum(now.day).y, fromDayNum(now.day).y + 1);
    var cutoff = opts.cutoffHour != null ? opts.cutoffHour : c.cutoffHour;
    var orderDay = now.day;
    if (isBusinessDay(orderDay, hol) && now.hour >= cutoff) orderDay += 1; // after the cutoff counts as tomorrow
    var rMin = opts.readyMin > 0 ? opts.readyMin : c.readyMin;
    var rMax = opts.readyMax >= rMin ? opts.readyMax : Math.max(rMin, c.readyMax);
    var mockup = addBusinessDays(orderDay, c.mockupDays, hol);
    var ok = addBusinessDays(mockup, c.approveDays, hol);
    var readyMin = addBusinessDays(ok, rMin, hol), readyMax = addBusinessDays(ok, rMax, hol);
    var region = zoneRegion(opts.zone);
    var out = { today: now.day, hour: now.hour, region: region, mockup: mockup, ok: ok, readyMin: readyMin, readyMax: readyMax, doorMin: null, doorMax: null };
    if (region !== 'far') {
      var tr = c.transitDays[region] != null ? c.transitDays[region] : c.transitDays.unknown;
      out.transit = tr;
      out.doorMin = addBusinessDays(readyMin, tr, hol);
      out.doorMax = addBusinessDays(readyMax, tr, hol);
    }
    return out;
  }

  // ---- words ----
  function md(n) { var p = fromDayNum(n); return MONTHS[p.m - 1] + ' ' + p.d; }
  function range(a, b) {
    if (a === b) return md(a);
    var pa = fromDayNum(a), pb = fromDayNum(b);
    return md(a) + ' to ' + (pa.m === pb.m && pa.y === pb.y ? pb.d : md(b));
  }
  function dayLabel(n) { var p = fromDayNum(n); return DAYS[p.wd] + ', ' + MONTHS[p.m - 1] + ' ' + p.d; }
  function words(r) {
    var t = CONFIG.text;
    return {
      main: r.doorMin != null ? t.door.replace('{range}', range(r.doorMin, r.doorMax)) : t.shipOnly.replace('{range}', range(r.readyMin, r.readyMax)),
      sub: t.sub.replace('{mockup}', dayLabel(r.mockup))
    };
  }

  // ---- page ----
  function renderAll() {
    if (typeof document === 'undefined') return;
    var els = document.querySelectorAll('[data-crew-ship-date]');
    if (!els.length) return;
    var zone = visitorZone(), now = new Date();
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      try {
        var r = compute(now, { zone: zone, readyMin: +el.getAttribute('data-ready-min') || 0, readyMax: +el.getAttribute('data-ready-max') || 0 });
        var w = words(r);
        var main = el.querySelector('[data-csd-main]'), sub = el.querySelector('[data-csd-sub]');
        if (!main || !sub) continue;
        if (main.textContent !== w.main) main.textContent = w.main;
        if (sub.textContent !== w.sub) sub.textContent = w.sub;
        el.setAttribute('data-csd-region', r.region);
        el.removeAttribute('data-csd-error');
        el.hidden = false;
      } catch (e) { el.hidden = true; el.setAttribute('data-csd-error', ''); } // never show a half-built or wrong line
    }
  }
  function boot() {
    if (typeof document === 'undefined') return;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', renderAll); else renderAll();
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') renderAll(); });
    setInterval(renderAll, 5 * 60 * 1000); // a tab left open past midnight moves to the next day
    // Dawn can redraw product blocks on a variant change; draw any fresh copy of the line.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, function () { setTimeout(renderAll, 0); });
      }
    } catch (e) { /* no pubsub on this page */ }
    try {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return; pending = true;
        setTimeout(function () { pending = false; if (document.querySelector('[data-crew-ship-date][hidden]:not([data-csd-error])')) renderAll(); }, 50);
      }).observe(document.body, { childList: true, subtree: true });
    } catch (e) { /* old browser: the line still draws on load */ }
  }

  return {
    CONFIG: CONFIG, compute: compute, words: words, range: range, dayLabel: dayLabel, iso: iso, dayNum: dayNum,
    parseIso: parseIso, shopNow: shopNow, easternFallback: easternFallback, rulesHolidays: rulesHolidays,
    holidaySet: holidaySet, isBusinessDay: isBusinessDay, addBusinessDays: addBusinessDays, zoneRegion: zoneRegion,
    renderAll: renderAll, boot: boot
  };
});
